/**
 * Language-aware URL helpers.
 *
 * URL scheme:
 * - French (default) → no prefix: `/features`, `/blog/xyz`
 * - Other 9 languages → prefixed: `/en/features`, `/ja/blog/xyz`
 *
 * This is the canonical prefixing pattern used by Stripe, Vercel, etc.
 */

export const SUPPORTED_LANGS = ['fr', 'en', 'es', 'de', 'it', 'pt', 'ru', 'zh', 'ar', 'ja'];
export const DEFAULT_LANG = 'fr';
export const PREFIXED_LANGS = SUPPORTED_LANGS.filter((l) => l !== DEFAULT_LANG);

/**
 * Parse a pathname into { lang, path, hasExplicitLang }.
 * - `hasExplicitLang` is TRUE only when the URL starts with a valid language prefix.
 * - When FALSE, callers should NOT force i18n to `lang` (let browser detection decide).
 *
 * Examples:
 *   "/en/features"       → { lang: "en", path: "/features", hasExplicitLang: true }
 *   "/features"          → { lang: "fr", path: "/features", hasExplicitLang: false }
 *   "/ja/blog/xyz"       → { lang: "ja", path: "/blog/xyz", hasExplicitLang: true }
 *   "/en"                → { lang: "en", path: "/", hasExplicitLang: true }
 *   "/"                  → { lang: "fr", path: "/", hasExplicitLang: false }
 */
export const parseLangPath = (pathname) => {
  const cleaned = (pathname || '/').replace(/\/+$/, '') || '/';
  const match = cleaned.match(/^\/([a-z]{2})(\/.*)?$/i);
  if (match && PREFIXED_LANGS.includes(match[1].toLowerCase())) {
    return { lang: match[1].toLowerCase(), path: match[2] || '/', hasExplicitLang: true };
  }
  return { lang: DEFAULT_LANG, path: cleaned, hasExplicitLang: false };
};

/**
 * Build a full URL path for a given language and content path.
 * Examples:
 *   buildLangPath("fr", "/features") → "/features"
 *   buildLangPath("en", "/features") → "/en/features"
 *   buildLangPath("ja", "/")         → "/ja"
 *   buildLangPath("fr", "/")         → "/"
 */
export const buildLangPath = (lang, path) => {
  const safePath = path && path.startsWith('/') ? path : `/${path || ''}`;
  const normalizedPath = safePath === '/' ? '' : safePath;
  if (lang === DEFAULT_LANG) {
    return normalizedPath || '/';
  }
  return `/${lang}${normalizedPath}` || `/${lang}`;
};

/**
 * Given the current section+slug, return the URL path for the requested language.
 * Used by the language switcher to swap language while staying on the same page.
 */
export const swapLangOnCurrentPath = (currentPathname, newLang) => {
  const { path } = parseLangPath(currentPathname);
  return buildLangPath(newLang, path);
};
